<script setup>
import { computed, onMounted, reactive, ref, watch, nextTick } from "vue";
import ProjectApi from "@api/projects.api";
import Project from "@models/project";
import { useValidation } from "@composables/useValidation";
import {
    required,
    maxLength,
    minValue,
    maxValue,
} from "@services/i18n-validators";
import { useRouter, useRoute } from "vue-router";
import { useLoaderStore } from "@store/loader";
import useForm from "@composables/Form";
import { ElNotification } from "element-plus";
import TagApi from "@api/tag.api";
import { useUserStore } from "@store/user.js";
import Storage from "@services/storage";
import ProjectBasics from "./create_project_steps/step1.vue";
import ProjectDetails from "./create_project_steps/step2.vue";
import ClientDetails from "./create_project_steps/step3.vue";
import ProjectCoordinates from "./create_project_steps/step4.vue";
import { useMetadataStore } from "@store/metadata";
import completedStep from "@/assets/img/completed-step.png";
import { defaultBusinessUnits } from "@/services/DefaultBusinessUnits";
const state = useMetadataStore();
let { formErrors, catchErrors } = useForm();
const router = useRouter();
const loaderStore = useLoaderStore();

const props = defineProps({
    proj: {
        type: Object,
        default: null,
    },
});
const emit = defineEmits(["create", "update"]);

const ENTITY_KEY = "__site_Pkms";
const savedState = Storage.get(ENTITY_KEY);
let openModal = ref(false);
let errors = reactive([]);
let newProject = reactive(new Project());
let project = computed(() => {
    if (props.proj) {
        console.log("my project:>>> ", props.proj);
        return props.proj;
    } else if (savedState) {
        return Object.assign(newProject, savedState);
    }

    return newProject;
});

// Step 4 helpers: set client-side errors and focus the first missing control
function getFirstFailingBusinessUnit() {
    const units = project.value?.business_units || [];
    const data = project.value?.business_units_data || {};
    for (let i = 0; i < units.length; i++) {
        const unitId = units[i];
        const unitData = data[unitId] || {};
        if (!Array.isArray(unitData.partners) || unitData.partners.length === 0)
            return { index: i, unitId, field: "partners" };
        if (
            !Array.isArray(unitData.associate_partners) ||
            unitData.associate_partners.length === 0
        )
            return { index: i, unitId, field: "associate_partners" };
        if (!Array.isArray(unitData.managers) || unitData.managers.length === 0)
            return { index: i, unitId, field: "mgr_0" };
        const hasTracks = Array.isArray(project.value?.tracks) && project.value.tracks?.length;
        if (hasTracks && Array.isArray(unitData.managers)) {
            const mgrTracks = Array.isArray(unitData.managers_tracks) ? unitData.managers_tracks : [];
            for (let j = 0; j < unitData.managers.length; j++) {
                const userId = unitData.managers[j];
                const trackId = mgrTracks[j];
                if (userId != null && userId !== "" && (trackId == null || trackId === "")) {
                    return { index: i, unitId, field: "mgr_track_0" };
                }
            }
        }
    }
    return null;
}

function syncStep4Errors() {
    const current = { ...(formErrors.value || {}) };
    // Clear previous step-4 related errors (per-unit keys)
    Object.keys(current).forEach((key) => {
        if (
            key.startsWith("partners_") ||
            key.startsWith("associate_partners_") ||
            key.startsWith("mgr_") ||
            key.startsWith("mgr_track_")
        ) {
            delete current[key];
        }
    });

    const units = project.value?.business_units || [];
    const data = project.value?.business_units_data || {};
    const hasTracks = Array.isArray(project.value?.tracks) && project.value.tracks?.length;

    for (let i = 0; i < units.length; i++) {
        const unitId = units[i];
        const unitData = data[unitId] || {};
        // partners required per unit
        if (!Array.isArray(unitData.partners) || unitData.partners.length === 0) {
            current[`partners_${unitId}`] = ["This field is required"];
        }
        // associate partners required per unit
        if (
            !Array.isArray(unitData.associate_partners) ||
            unitData.associate_partners.length === 0
        ) {
            current[`associate_partners_${unitId}`] = ["This field is required"];
        }
        // at least one manager required per unit
        const mgrs = Array.isArray(unitData.managers)
            ? unitData.managers.filter((m) => m != null && m !== "")
            : [];
        if (mgrs.length === 0) {
            current[`mgr_0_${unitId}`] = ["This field is required"];
        }
        // if tracks are configured, a selected manager must have a track
        if (hasTracks && Array.isArray(unitData.managers)) {
            const mgrTracks = Array.isArray(unitData.managers_tracks)
                ? unitData.managers_tracks
                : [];
            for (let j = 0; j < unitData.managers.length; j++) {
                const userId = unitData.managers[j];
                const trackId = mgrTracks[j];
                if (userId != null && userId !== "" && (trackId == null || trackId === "")) {
                    current[`mgr_track_${j}_${unitId}`] = ["This field is required"];
                    break; // only flag first missing track per unit
                }
            }
        }
    }

    formErrors.value = current;
}

async function focusStep4FirstMissing() {
    const fail = getFirstFailingBusinessUnit();
    if (!fail) return;
    await nextTick();
    const index = fail.index || 0;
    const nameForField = fail.field;
    const selectNth = (selector, n) => {
        const nodes = document.querySelectorAll(selector);
        return nodes && nodes[n] ? nodes[n] : null;
    };
    let el = null;
    if (nameForField === "partners") {
        el = selectNth('[name="partners"] input.vs__search', index) || selectNth('[name="partners"]', index);
    } else if (nameForField === "associate_partners") {
        el = selectNth('[name="associate_partners"] input.vs__search', index) || selectNth('[name="associate_partners"]', index);
    } else if (nameForField === "mgr_0") {
        el = selectNth('[name="mgr_0"] input.vs__search', index) || selectNth('[name="mgr_0"]', index);
    } else if (nameForField === "mgr_track_0") {
        el = selectNth('[name="mgr_track_0"] input.vs__search', index) || selectNth('[name="mgr_track_0"]', index);
    }
    if (el && typeof el.focus === "function") {
        try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (e) {}
        el.focus();
    }
}

function formatErrorsForNotification(errs) {
    if (!errs || typeof errs !== "object") return "";
    const buMap = {};
    (businessUnitsForSelect.value || []).forEach((bu) => {
        buMap[String(bu.id)] = bu.name;
    });
    const toTitle = (s) =>
        (s || "")
            .replace(/_/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .replace(/\b\w/g, (c) => c.toUpperCase());
    const msgs = [];
    for (const [key, val] of Object.entries(errs)) {
        if (key.startsWith("business_units_data.")) {
            const m = key.match(/^business_units_data\.([^\.]+)\.(.+)$/);
            if (m) {
                const unitId = m[1];
                const tail = m[2];
                const buName = buMap[String(unitId)] || String(unitId);
                let label = tail;
                if (tail.startsWith("partners")) label = "Partners";
                else if (tail.startsWith("associate_partners")) label = "Associate Partners";
                else if (tail.startsWith("managers")) {
                    // If tracks mode is enabled, unify managers-related errors into one friendly message
                    const hasTracks =
                        Array.isArray(project.value?.tracks) &&
                        project.value.tracks?.length;
                    if (hasTracks) {
                        msgs.push("Manager and tracks are required");
                        continue;
                    }
                    // Fallback labels when tracks are not used
                    if (tail.includes("track_id")) {
                        label = "Manager Track";
                    } else {
                        label = "Manager";
                    }
                }
                else if (tail.startsWith("members") && tail.includes("track_id")) label = "Member Track";
                else if (tail.startsWith("members")) label = "Member";
                else label = toTitle(tail.replace(/\..*$/, ""));
                msgs.push(`${label} is required`);
                continue;
            }
        }
        const fallback = Array.isArray(val) ? val[0] : String(val);
        msgs.push(fallback || `${toTitle(key.replace(/\./g, " "))} is invalid`);
    }
    const MAX = 4;
    if (msgs.length <= MAX) return msgs.join("\n");
    return `${msgs.slice(0, MAX).join("\n")}\n+${msgs.length - MAX} more`;
}

// Normalize incoming edit-mode data immediately so children render correctly
if (props.proj) {
    try {
        // Normalize has_multiple_tracks to boolean
        project.value.has_multiple_tracks = Boolean(
            Number(project.value.has_multiple_tracks) ||
                project.value.has_multiple_tracks === true
        );

        // Normalize tracksCount to a number (integer)
        if (project.value.has_multiple_tracks) {
            const count = Number(project.value.tracksCount);
            project.value.tracksCount = Number.isFinite(count) ? count : 0;
        } else {
            project.value.tracksCount = null;
        }

        // Normalize tracks to array of strings sized to tracksCount
        if (project.value.has_multiple_tracks) {
            if (!Array.isArray(project.value.tracks)) project.value.tracks = [];
            project.value.tracks = project.value.tracks.map((t) => {
                const raw =
                    typeof t === "string" ? t : t?.name ?? t?.label ?? "";
                return String(raw);
            });
            const desired = Number(project.value.tracksCount) || 0;
            if (project.value.tracks.length > desired) {
                project.value.tracks = project.value.tracks.slice(0, desired);
            } else {
                while (project.value.tracks.length < desired) {
                    project.value.tracks.push("");
                }
            }
        } else {
            project.value.tracks = [];
        }

        // Normalize tags to array of IDs for form-el-select
        if (Array.isArray(project.value.tags)) {
            project.value.tags = project.value.tags
                .map((t) => (typeof t === "object" ? t?.id ?? t?.value : t))
                .filter((id) => id !== undefined && id !== null);
        }
    } catch (e) {
        console.warn("Normalization error (edit mode):", e);
    }
}

const isUpdateForm = !!project.value.id;

const disabled = ref(false);
const options = ref([]);
const formContainerRef = ref(null);

// Gate when to show validation errors for select components
const showErrorsStep1 = ref(false);
const showErrorsStep3 = ref(false);
const showErrorsStep4 = ref(false);

// Custom validator for tracksCount when has_multiple_tracks is true
const tracksCountValidator = (value, siblings) => {
    // If has_multiple_tracks is false, tracksCount is not required
    if (!siblings.has_multiple_tracks) {
        return true;
    }

    // If has_multiple_tracks is true, tracksCount must be at least 1
    return value && value >= 1;
};

// Custom validator for tracks when has_multiple_tracks is true
const tracksValidator = (value, siblings) => {
    // If has_multiple_tracks is false, tracks are not required
    if (!siblings.has_multiple_tracks) {
        return true;
    }

    // If has_multiple_tracks is true, validate tracks
    if (!Array.isArray(value)) {
        return false;
    }

    // Check if all tracks up to tracksCount are filled
    const tracksCount = siblings.tracksCount || 0;
    for (let i = 0; i < tracksCount; i++) {
        const trackName =
            typeof value[i] === "string" ? value[i] : value[i]?.name;
        if (!trackName || trackName.trim() === "") {
            return false;
        }
    }

    // Check for uniqueness - ensure no duplicate track names
    const filledTracks = value.slice(0, tracksCount).map((track) => {
        // Handle both string tracks (create mode) and object tracks (edit mode)
        const trackName = typeof track === "string" ? track : track.name;
        return trackName.trim().toLowerCase();
    });
    const uniqueTracks = new Set(filledTracks);

    if (filledTracks.length !== uniqueTracks.size) {
        return false;
    }

    return true;
};

// Custom validator for managed_service_id - only required when business unit 2 is selected
const managedServiceValidator = (value, siblings) => {
    const businessUnits = siblings.business_units || [];
    // If business unit 2 is not selected, managed_service_id is not required
    if (!Array.isArray(businessUnits) || !businessUnits.includes(2)) {
        return true;
    }
    // If business unit 2 is selected, managed_service_id is required
    return value != null && value !== "";
};

// Custom validator for digital_solution_id - only required when business unit 2 is selected
const digitalSolutionValidator = (value, siblings) => {
    const businessUnits = siblings.business_units || [];
    // If business unit 2 is not selected, digital_solution_id is not required
    if (!Array.isArray(businessUnits) || !businessUnits.includes(2)) {
        return true;
    }
    // If business unit 2 is selected, digital_solution_id is required
    return value != null && value !== "";
};

const rules = reactive({
    title: { required, maxLength: maxLength(125) },
    charge_code: { required },
    start_date: { required },
    end_date: { required },
    offering_ids: { required },
    business_domain_ids: { required },
    // service_line_ids: { required },
    digital_solution_id: { digitalSolutionValidator },
    managed_service_id: { managedServiceValidator },
    // document_type_ids: { required },
    // docs_count: { required },
    secondary_docs_count: { required },
    business_units: { required },
    has_multiple_tracks: { required },
    tracksCount: {
        tracksCountValidator,
        maxValue: maxValue(10),
        minValue: minValue(1),
    },
    tracks: { tracksValidator },

    project_name: { required, maxLength: maxLength(125) },
    business_context_background: { required },
    client_challenge: { required },
    tam_mandate: { required },
    expected_results_impact: { required },
    tags: { required },

    client_id: { required },
    industry_id: { required },

    // Step 4 validation
    business_units_data: { required },
});

const { v$, $externalResults } = useValidation(rules, project);

// const step = ref(isUpdateForm ? 2 : 1);
const step = ref(1);

// const chargeCodesForSelect = ref([]);
const tags = ref([]);
let fileError = ref("");
let userId = computed(() => {
    return useUserStore().user.id;
});

const metadata = computed(() => state.getMetaData);
const clientTypesForSelect = computed(() => metadata.value.clientTypes ?? []);
// const documentTypesForSelect = computed(
//     () => metadata.value.documentTypes ?? []
//     // .filter(
//     //     (dt) => dt.is_codification === 0
//     // )
// );


const managedServiceForSelect = computed(
    () => metadata.value.managedService ?? []
);
const clientsForSelect = computed(() => metadata.value.clients ?? []);
const industriesForSelect = computed(() => metadata.value.industries ?? []);
const serviceLinesForSelect = computed(() => metadata.value.serviceLines ?? []);
const usersForSelect = computed(() => metadata.value.users ?? []);
const businessUnitsForSelect = computed(
    // () => metadata.value.businessUnit ?? []
    () => defaultBusinessUnits ?? []
);
const digitalSolutionForSelect = computed(
    () => metadata.value.digitalSolution ?? []
);
const serviceOfferingForSelect = computed(() => metadata.value.offering ?? []);
const defaultPartnersForSelect = computed(
    () => metadata.value.defaultPartners ?? []
);

const businessDomainsForSelect = computed(
    () => metadata.value.businessDomains ?? []
);

// const businessDomainsForSelect = computed(() => {
//     metadata.value.businessDomains ?? [];

// if (!project.value.offering_id) {
//     return [];
// }

// // Filter business domains based on the selected offering
// return allBusinessDomains.filter(
//     (domain) =>
//         domain.offering_id === project.value.offering_id
// );
// });

const isStep1Valid = computed(() => {
    // Basic field validation
    const basicValidation =
        !v$.value.title.$invalid &&
        !v$.value.charge_code.$invalid &&
        !v$.value.start_date.$invalid &&
        !v$.value.end_date.$invalid &&
        !v$.value.offering_ids.$invalid &&
        !v$.value.business_domain_ids.$invalid &&
        // !v$.value.service_line_ids.$invalid &&
        !v$.value.digital_solution_id.$invalid &&
        !v$.value.managed_service_id.$invalid &&
        // !v$.value.document_type_ids.$invalid &&
        // !v$.value.docs_count.$invalid &&
        !v$.value.secondary_docs_count.$invalid &&
        !v$.value.business_units.$invalid &&
        !v$.value.has_multiple_tracks.$invalid;

    // If has_multiple_tracks is false, only basic validation is needed
    if (!project.value.has_multiple_tracks) {
        return basicValidation;
    }

    // If has_multiple_tracks is true, also validate tracks
    if (project.value.has_multiple_tracks) {
        return (
            basicValidation &&
            !v$.value.tracksCount.$invalid &&
            !v$.value.tracks.$invalid
        );
    }

    return basicValidation;
});

const isStep2Valid = computed(() => {
    return (
        !v$.value.project_name.$invalid &&
        !v$.value.business_context_background.$invalid &&
        !v$.value.client_challenge.$invalid &&
        !v$.value.tam_mandate.$invalid &&
        !v$.value.expected_results_impact.$invalid
    );
});

const isStep3Valid = computed(() => {
    return !v$.value.client_id.$invalid;
});

const isStep4Valid = computed(() => {
    // Check if business units are selected
    if (
        !project.value.business_units ||
        project.value.business_units.length === 0
    ) {
        return false;
    }

    // Check if business_units_data exists and has data for each business unit
    if (!project.value.business_units_data) {
        return false;
    }

    // Validate each business unit has required data
    for (const unit of project.value.business_units) {
        const unitData = project.value.business_units_data[unit];
        if (!unitData) {
            return false;
        }

        // Check if at least one partner is selected
        if (!unitData.partners || unitData.partners.length === 0) {
            return false;
        }

        // Check if at least one associate partner is selected
        if (
            !unitData.associate_partners ||
            unitData.associate_partners.length === 0
        ) {
            return false;
        }

        // Check if at least one manager is selected
        if (!unitData.managers || unitData.managers.length === 0) {
            return false;
        }

        // Check if at least one member is selected
        if (!unitData.members || unitData.members.length === 0) {
            return false;
        }

        // Note: Managers and members can be added without tracks even if tracks are available
    }

    return true;
});

// Check for duplicate tracks
const hasDuplicateTracks = computed(() => {
    if (
        !project.value.has_multiple_tracks ||
        !project.value.tracks ||
        !project.value.tracksCount
    ) {
        return false;
    }

    const filledTracks = project.value.tracks
        .slice(0, Number(project.value.tracksCount) || 0)
        .map((track) => {
            const raw =
                typeof track === "string"
                    ? track
                    : track?.name ?? track?.label ?? "";
            return String(raw).trim().toLowerCase();
        })
        .filter((t) => t !== "");

    const uniqueTracks = new Set(filledTracks);
    return filledTracks.length !== uniqueTracks.size;
});

const canGoNext = computed(() => {
    if (step.value === 1) return isStep1Valid.value;
    if (step.value === 2) return isStep2Valid.value;
    if (step.value === 3) return isStep3Valid.value;
    if (step.value === 4) return isStep4Valid.value;
    return false;
});

const onUploadSuccess = (response) => {
    // console.log(response, file, fileList);
    fileError.value = "";
    project.value.avatar = response.file.id;
};
const onUploadError = (response) => {
    fileError.value = "";
    fileError.value = response.data.message;
};
const remoteMethod = (query) => {
    if (query) {
        TagApi.list({ Keyword: query, page: 0, limit: 100 }).then((res) => {
            options.value = res.data.data
                .filter((item) => {
                    return item.name
                        .toLowerCase()
                        .includes(query.toLowerCase());
                })
                .map((item) => {
                    return {
                        value: item.id,
                        label: item.name,
                    };
                });
        });
    } else {
        options.value = [];
    }
};

// Watch for offering changes to auto-select all business domains
// watch(
//     () => project.value.offering_id,
//     (newServiceOfferingId, oldServiceOfferingId) => {
//         // Auto-select all business domains when managed service changes
//         if (
//             newServiceOfferingId !== oldServiceOfferingId &&
//             newServiceOfferingId
//         ) {
//             const allBusinessDomains = metadata.value.businessDomains ?? [];
//             const filteredDomains = allBusinessDomains.filter(
//                 (domain) => domain.offering_id === newServiceOfferingId
//             );
//             // Select ALL domain IDs for the selected managed service
//             project.value.business_domain_ids = filteredDomains.map(
//                 (domain) => domain.id
//             );
//             console.log(
//                 "Auto-selected business domains:",
//                 filteredDomains.map((d) => d.name),
//                 "IDs:",
//                 project.value.business_domain_ids
//             );
//         } else if (!newServiceOfferingId) {
//             // Clear selection if no managed service selected
//             project.value.business_domain_ids = [];
//         }
//     },
//     { immediate: true }
// );

// Watch for business_units changes to clear managed_service_id and digital_solution_id when business unit 2 is deselected
watch(
    () => project.value.business_units,
    (newBusinessUnits, oldBusinessUnits) => {
        const hadBusinessUnit2 = Array.isArray(oldBusinessUnits) && oldBusinessUnits.includes(2);
        const hasBusinessUnit2 = Array.isArray(newBusinessUnits) && newBusinessUnits.includes(2);
        
        // If business unit 2 was deselected, clear managed_service_id and digital_solution_id
        if (hadBusinessUnit2 && !hasBusinessUnit2) {
            project.value.managed_service_id = null;
            project.value.digital_solution_id = null;
        }
    },
    { deep: true }
);

// Watch for has_multiple_tracks changes to clear member/manager data when switching tracks mode
watch(
    () => project.value.has_multiple_tracks,
    (newValue, oldValue) => {
        // If switching from false to true (no tracks to has tracks), clear existing member/manager data
        if (oldValue === false && newValue === true) {
            // Clear business units data to remove old member/manager selections
            if (project.value.business_units_data) {
                Object.keys(project.value.business_units_data).forEach(
                    (unitKey) => {
                        const unitData =
                            project.value.business_units_data[unitKey];
                        if (unitData) {
                            // Clear managers and members data but keep partners
                            unitData.managers = [];
                            unitData.managers_tracks = [];
                            unitData.members = [];
                            unitData.members_tracks = [];
                        }
                    }
                );
            }
        }

        // If switching from true to false (has tracks to no tracks), clear tracks data
        if (oldValue === true && newValue === false) {
            // Clear tracks data
            project.value.tracks = [];
            project.value.tracksCount = null;

            // Clear track-related data from business units
            if (project.value.business_units_data) {
                Object.keys(project.value.business_units_data).forEach(
                    (unitKey) => {
                        const unitData =
                            project.value.business_units_data[unitKey];
                        if (unitData) {
                            // Clear track-related data
                            unitData.managers_tracks = [];
                            unitData.members_tracks = [];
                        }
                    }
                );
            }
        }
    }
);

watch(
    project,
    (newState) => {
        // Create a plain shallow copy for persistence to avoid mutating reactive source
        const snapshot = JSON.parse(JSON.stringify(newState || {}));

        // Remove empty-ish values from the snapshot ONLY (do not mutate `project`)
        Object.keys(snapshot).forEach((propName) => {
            if (
                propName !== "start_date" &&
                propName !== "end_date" &&
                isEmptyValues(snapshot[propName])
            ) {
                delete snapshot[propName];
            }
        });

        // Persist without causing reactive churn
        Storage.set(ENTITY_KEY, snapshot);
    },
    { deep: true });

// Auto-clear select errors in Step 1 as soon as user fixes them
watch(
    () => [
        project.value.managed_service_id,
        project.value.digital_solution_id,
        project.value.offering_ids,
        project.value.business_domain_ids,
        project.value.business_units,
    ],
    () => {
        // Only sync for step 1 fields
        if (showErrorsStep1.value) {
            syncClientFormErrorsForStep(1);
        }
    },
    { deep: true }
);

// Auto-clear client select error in Step 3
watch(
    () => project.value.client_id,
    () => {
        if (showErrorsStep3.value) {
            syncClientFormErrorsForStep(3);
        }
    }
);

// Keep Step 4 per-unit errors in sync as selections change
watch(
    () => project.value.business_units_data,
    () => {
        if (showErrorsStep4.value) {
            syncStep4Errors();
        }
    },
    { deep: true }
);

// Centralized step fields mapping used for validation and navigation
const STEP_FIELDS = {
    1: [
        "title",
        "charge_code",
        "start_date",
        "end_date",
        "offering_ids",
        "business_domain_ids",
        // "service_line_ids",
        "digital_solution_id",
        "managed_service_id",
        // "document_type_ids",
        // "docs_count",
        "secondary_docs_count",
        "business_units",
        "has_multiple_tracks",
        "tracksCount",
        "tracks",
    ],
    2: [
        "project_name",
        "business_context_background",
        "client_challenge",
        "tam_mandate",
        "expected_results_impact",
        "tags",
    ],
    3: ["client_id"],
    4: ["business_units_data"],
};

function touchStepFields(stepNumber) {
    const fields = STEP_FIELDS[stepNumber] || [];
    fields.forEach((fieldName) => {
        const ctrl = v$.value?.[fieldName];
        if (ctrl && typeof ctrl.$touch === "function") ctrl.$touch();
    });
}

async function focusFirstInvalidField(stepNumber) {
    const fields = STEP_FIELDS[stepNumber] || [];
    const firstInvalid = fields.find((f) => v$.value?.[f]?.$invalid);
    if (!firstInvalid) return;
    await nextTick();
    const selectors = [
        `[name="${firstInvalid}"]`,
        `#${firstInvalid}`,
        `#${firstInvalid} .vs__search`,
        `[name="${firstInvalid}"] .vs__search`,
        `#${firstInvalid} input`,
        `[data-field="${firstInvalid}"]`,
        `[data-model="${firstInvalid}"]`,
        `[aria-label="${firstInvalid}"]`,
    ];
    for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (el && typeof el.focus === "function") {
            try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (e) {}
            el.focus();
            break;
        }
    }
}

// Only certain fields (mostly selects using FormElSelect) read errors from formErrors.
const FORM_ERRORS_FIELDS = {
    1: [
        "managed_service_id",
        "digital_solution_id",
        "offering_ids",
        "business_domain_ids",
        "business_units",
    ],
    2: [],
    3: ["client_id"],
    4: ["partners", "associate_partners", "managers", "members"],
};

function syncClientFormErrorsForStep(stepNumber) {
    const fields = FORM_ERRORS_FIELDS[stepNumber] || [];
    const current = { ...(formErrors.value || {}) };
    fields.forEach((fieldName) => {
        const ctrl = v$.value?.[fieldName];
        if (!ctrl) return;
        if (typeof ctrl.$touch === "function") ctrl.$touch();
        if (ctrl.$invalid) {
            const msg = ctrl.$errors?.[0]?.$message || "This field is required";
            current[fieldName] = [msg];
        } else if (current[fieldName]) {
            delete current[fieldName];
        }
    });
    formErrors.value = current;
}

function navigateToErrorStep(errors) {
    // Find which step has errors
    for (let stepNumber = 1; stepNumber <= 4; stepNumber++) {
        const stepFieldNames = STEP_FIELDS[stepNumber];
        const hasErrorsInStep = stepFieldNames.some(
            (fieldName) => errors[fieldName] && errors[fieldName].length > 0
        );

        if (hasErrorsInStep) {
            step.value = stepNumber;
            break;
        }
    }
}

function submit() {
    // Always run validation for full form
    if (v$.value.$validate()) {
        disabled.value = true;
        save();
    } else {
        // If on step 4 and invalid, surface errors and focus first missing
        if (step.value === 4) {
            showErrorsStep4.value = true;
            syncStep4Errors();
            focusStep4FirstMissing();
        } else {
            // Touch current step so errors show
            if (step.value === 1) showErrorsStep1.value = true;
            if (step.value === 3) showErrorsStep3.value = true;
            touchStepFields(step.value);
            syncClientFormErrorsForStep(step.value);
            focusFirstInvalidField(step.value);
        }
    }
}

function save() {
    let req = "";
    if (isUpdateForm) {
        req = ProjectApi.update(project.value);
    } else {
        project.created_by = userId;
        req = ProjectApi.store(project.value);
    }

    req.then(({ data }) => {
        if (isUpdateForm) {
            ElNotification({
                title: "Success",
                message: "Project has been updated successfully!",
                type: "success",
                position: "bottom-left",
                duration: 10000,
            });
            router.go(0);
            emit("update");
        } else {
            Storage.remove(ENTITY_KEY);
            emit("create", data.data.project);
        }
    })
        .catch((error) => {
            console.log("error in request", error?.response);
            const serverErrors = error?.response?.data?.errors || {};
            const friendly = formatErrorsForNotification(serverErrors);
            ElNotification({
                message:
                    (friendly && friendly.replace(/\n/g, "<br />")) ||
                    error?.response?.data?.message ||
                    "Could not save project.",
                type: "error",
                position: "bottom-left",
                duration: 20000,
                dangerouslyUseHTMLString: true,
            });
            $externalResults.value = serverErrors;
            catchErrors(serverErrors);
            // Navigate to the step with validation errors
            if (serverErrors && Object.keys(serverErrors).length) {
                navigateToErrorStep(serverErrors);
            }
        })
        .finally(() => {
            disabled.value = false;
        });
}

function isEmptyValues(value) {
    return (
        value === undefined ||
        value === null ||
        value === NaN ||
        (typeof value === "object" && Object.keys(value).length === 0) ||
        (typeof value === "string" && value.trim().length === 0)
    );
}

async function nextStep() {
    if (step.value >= 4) return;
    // Touch fields for the current step to reveal validation messages
    if (step.value === 1) showErrorsStep1.value = true;
    if (step.value === 3) showErrorsStep3.value = true;
    if (step.value === 4) showErrorsStep4.value = true;
    touchStepFields(step.value);
    // Populate client-side errors for selects and clear resolved ones
    syncClientFormErrorsForStep(step.value);
    if (canGoNext.value) {
        // Clear any lingering errors for this step before advancing
        syncClientFormErrorsForStep(step.value);
        step.value++;
        // scroll to the top of the modal content
        await nextTick();
        const container =
            (formContainerRef && formContainerRef.value) ||
            document.querySelector(".form-container");
        if (container && typeof container.scrollTo === "function") {
            container.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    } else {
        // Focus the first invalid field in the current step
        focusFirstInvalidField(step.value);
    }

}

async function prevStep() {
    if (step.value > 1) {
        // Clear gating for steps beyond the one we are moving to
        if (step.value === 4) showErrorsStep4.value = false;
        if (step.value === 3) showErrorsStep3.value = false;
        step.value--;
        // If returning to step 1, optionally hide its errors until user proceeds again
        if (step.value === 1) {
            // Keep step1 errors if you want to persist; set to false to hide:
            // showErrorsStep1.value = false;
        }
        await nextTick();
        const container =
            (formContainerRef && formContainerRef.value) ||
            document.querySelector(".form-container");
        if (container && typeof container.scrollTo === "function") {
            container.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }
}

onMounted(() => {
    Storage.remove(ENTITY_KEY);
    Object.assign(newProject, new Project());
    v$.value.$reset();

    remoteMethod();

    // if (project.value.tags) {
    //     // Convert tags to the format expected by FormElSelect
    //     // FormElSelect expects an array of IDs for the model value
    //     project.value.tags = project.value.tags.map((tag) => {
    //         if (typeof tag === 'object' && tag.id) {
    //             return tag.id; // Return just the ID for the model
    //         }
    //         return tag; // Return as-is if already an ID
    //     });
    // }

    // Ensure tags are IDs (expected by FormElSelect model binding)
    if (Array.isArray(project.value.tags)) {
        project.value.tags = project.value.tags
            .map((t) => (typeof t === "object" ? t?.id ?? t?.value : t))
            .filter((id) => id !== undefined && id !== null);
    }

    // normalize boolean for has_multiple_tracks, and ensure tracks length
    project.value.has_multiple_tracks = Boolean(
        Number(project.value.has_multiple_tracks) ||
            project.value.has_multiple_tracks === true
    );
    if (project.value.has_multiple_tracks) {
        if (!Array.isArray(project.value.tracks)) project.value.tracks = [];
        // Normalize existing track entries to strings
        project.value.tracks = project.value.tracks.map((t) => {
            const raw = typeof t === "string" ? t : t?.name ?? t?.label ?? "";
            return String(raw);
        });

        // Ensure tracksCount is a number
        if (!Number.isFinite(Number(project.value.tracksCount))) {
            project.value.tracksCount = 0;
        } else {
            project.value.tracksCount = Number(project.value.tracksCount);
        }
        const desired = project.value.tracksCount || 0;

        // Convert object tracks to string tracks for consistency
        project.value.tracks = project.value.tracks.map((track) => {
            return typeof track === "string" ? track : track.name || "";
        });

        if (project.value.tracks.length > desired) {
            project.value.tracks = project.value.tracks.slice(0, desired);
        } else {
            while (project.value.tracks.length < desired) {
                project.value.tracks.push("");
            }
        }
    }
});
</script>

<template>
    <div class="create-project-container">
        <!-- Loading Spinner -->
        <div v-if="loaderStore.isLoading" class="loading-overlay">
            <div class="loading-spinner"></div>
        </div>

        <!-- Main Container -->
        <div class="main-wrapper">
            <!-- Step Progress Indicator -->
            <div class="progress-section wizard-border wizard-header">
                <div
                    v-for="(stepName, index) in [
                        'Project Basics',
                        'Project Details',
                        'Client Details',
                        'Staffing / Teams',
                    ]"
                    :key="index"
                    class="step-item"
                >
                    <!-- Step Circle -->
                    <div
                        :class="[
                            'step-circle',
                            step > index + 1
                                ? 'step-completed'
                                : step === index + 1
                                ? 'step-active'
                                : 'step-inactive',
                        ]"
                    >
                        <template v-if="step > index + 1">
                            <img :src="completedStep" alt="Completed" />
                        </template>
                        <template v-else>
                            {{ index + 1 }}
                        </template>
                    </div>

                    <!-- Step Label -->
                    <span
                        :style="
                            step > index + 1
                                ? 'color: #17B26A;'
                                : step === index + 1
                                ? 'color: #007bff;'
                                : 'color: #6c757d;'
                        "
                        :class="[
                            'step-label',
                            step > index + 1
                                ? 'step-label-completed'
                                : step === index + 1
                                ? 'step-label-active'
                                : 'step-label-inactive',
                        ]"
                    >
                        {{ stepName }}
                    </span>

                    <!-- Connector Line (except for last step) -->
                    <!-- <div
                        v-if="index < 3"
                        :class="[
                            'step-connector',
                            step > index + 1
                                ? 'step-connector-active'
                                : 'step-connector-inactive',
                        ]"
                    ></div> -->
                </div>
            </div>

            <!-- Form Container -->
            <div class="form-container" ref="formContainerRef">
                <!-- Step Content -->
                <div class="step-content">
                    <ProjectBasics
                        v-if="step === 1"
                        :project="project"
                        :v$="v$"
                        :formErrors="formErrors"
                        :is-update-form="isUpdateForm"
                        :business-units-for-select="businessUnitsForSelect"
                        :service-offering-for-select="serviceOfferingForSelect"
                        :business-domains-for-select="businessDomainsForSelect"
                        :digital-solution-for-select="digitalSolutionForSelect"
                        :service-lines-for-select="serviceLinesForSelect"
                        :managed-service-for-select="managedServiceForSelect"
                        :has-duplicate-tracks="hasDuplicateTracks"
                    />
                    <ProjectDetails
                        v-if="step === 2"
                        :project="project"
                        :v$="v$"
                        :formErrors="formErrors"
                    />
                    <ClientDetails
                        v-if="step === 3"
                        :project="project"
                        :v$="v$"
                        :formErrors="formErrors"
                        :clients-for-select="clientsForSelect"
                        :industries-for-select="industriesForSelect"
                        :client-types-for-select="clientTypesForSelect"
                    />
                    <ProjectCoordinates
                        v-if="step === 4"
                        :project="project"
                        :v$="v$"
                        :formErrors="formErrors"
                        :business-units-for-select="businessUnitsForSelect"
                        :team-for-select="usersForSelect"
                        :default-partners-for-select="defaultPartnersForSelect"
                    />
                </div>
            </div>

            <!-- Navigation Buttons -->
            <div
                :class="{
                    'justify-content-end': step === 1,
                    'justify-content-between': step !== 1,
                }"
                class="navigation-section wizard-footer"
            >
                <!-- <div class="nav-button-group"> -->
                <button
                    @click="prevStep"
                    v-if="step !== 1"
                    :class="[
                        'nav-button',
                        'nav-button-back',
                        step === 1 ? 'nav-button-disabled' : '',
                    ]"
                >
                    Back
                </button>
                <!-- <button
                    v-if="step < 4"
                    @click="nextStep"
                    :disabled="!canGoNext"
                    :class="[
                        'nav-button',
                        'nav-button-next',
                        !canGoNext ? 'nav-button-disabled' : '',
                    ]"
                >
                    Proceed to Step {{ step + 1 }}
                </button> -->
                <button
                    v-if="step < 4"
                    @click="nextStep"
                    :class="[
                        'nav-button',
                        'nav-button-next'
                    ]"
                >
                    {{
                        step === 1
                            ? "Proceed to Project Details"
                            : step === 2
                            ? "Proceed to Client Details"
                            : step === 3
                            ? "Proceed to Staffing / Teams"
                            : "Save Project"
                    }}
                </button>
                <button
                    class="btn btn-primary"
                    v-else
                    type="button"
                    @click="submit()"
                    :disabled="disabled"
                    :class="[
                        'nav-button',
                        'nav-button-save',
                        disabled ? 'nav-button-disabled' : '',
                    ]"
                >
                    <span v-if="disabled" class="save-loading">
                        <div class="save-spinner"></div>
                        Saving...
                    </span>
                    <span v-else>Save Project</span>
                </button>
                <!-- </div> -->
            </div>
        </div>
    </div>
</template>

<style scoped>
.save-disabled {
    color: #6c757d;
    font-style: italic;
}
.main-wrapper {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 160px);
}

.wizard-header {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #fff;
}

.form-container {
    overflow: auto;
    flex: 1 1 auto;
}

.wizard-footer {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: #fff;
    padding-top: 12px;
}
</style>
